import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import { getCardsByIds } from '../../utils/domainCardLoader';
import { DomainCard } from '../../types/domain-card';
import './CharacterSummary.css';

interface CharacterSummaryProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const CharacterSummary: React.FC<CharacterSummaryProps> = ({
  characterData,
  onDataChange,
}) => {
  const [name, setName] = useState(characterData.name || '');
  const [ancestryData, setAncestryData] = useState<any>(null);
  const [communityData, setCommunityData] = useState<any>(null);
  const [classData, setClassData] = useState<any>(null);
  const [subclassData, setSubclassData] = useState<any>(null);
  const [domainCards, setDomainCards] = useState<DomainCard[]>([]);

  // Load data for display
  useEffect(() => {
    const loadDisplayData = async () => {
      try {
        // Load ancestry data
        if (characterData.ancestry) {
          const ancestriesData = await import('../../data/ancestries.json');
          const ancestry = ancestriesData.default.find((a: any) => a.id === characterData.ancestry);
          setAncestryData(ancestry);
        }

        // Load community data
        if (characterData.community) {
          const communitiesData = await import('../../data/communities.json');
          const community = communitiesData.communities.find((c: any) => c.id === characterData.community);
          setCommunityData(community);
        }

        // Load class data
        if (characterData.class) {
          const { findClassById, findSubclass } = await import('../../utils/classDataLoader');
          const classInfo = await findClassById(characterData.class);
          setClassData(classInfo);

          if (characterData.subclass && classInfo) {
            const subclass = await findSubclass(characterData.class, characterData.subclass);
            setSubclassData(subclass);
          }
        }

        // Load domain cards data
        if (characterData.domainCards && characterData.domainCards.length > 0) {
          const cardIds = characterData.domainCards.map(ref => ref.contentId);
          const cards = await getCardsByIds(cardIds);
          setDomainCards(cards);
        }
      } catch (error) {
        console.error('Failed to load character data for summary:', error);
      }
    };

    loadDisplayData();
  }, [characterData.ancestry, characterData.community, characterData.class, characterData.subclass, characterData.domainCards]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    onDataChange({ name: newName });
  };

  // Capitalize first letter of string
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <div className="character-summary">
      <p className="character-summary__description">
        Review your character choices and give them a name. This summary shows all your selected 
        options and their key features.
      </p>

      <div className="character-summary__content">
        {/* Character Overview */}
        <div className="character-overview">
          <h3>Character Summary</h3>
          
          <div className="summary-grid">
            <div className="summary-item">
              <h4>Ancestry</h4>
              <div className="summary-value">
                {ancestryData ? ancestryData.name : capitalize(characterData.ancestry || 'Not selected')}
              </div>
              {ancestryData && (
                <div className="summary-features">
                  <strong>Features:</strong> {ancestryData.features.map((f: any) => f.name).join(', ')}
                </div>
              )}
            </div>

            <div className="summary-item">
              <h4>Community</h4>
              <div className="summary-value">
                {communityData ? communityData.name : capitalize(characterData.community || 'Not selected')}
              </div>
              {communityData && (
                <div className="summary-features">
                  <strong>Feature:</strong> {communityData.feature.name}
                </div>
              )}
            </div>

            <div className="summary-item">
              <h4>Class</h4>
              <div className="summary-value">
                {classData ? classData.name : capitalize(characterData.class || 'Not selected')}
              </div>
              {classData && (
                <div className="summary-features">
                  <strong>Domains:</strong> {classData.domains.join(', ')}<br/>
                  <strong>Starting HP:</strong> {classData.startingHitPoints} | <strong>Evasion:</strong> {classData.startingEvasion}
                </div>
              )}
            </div>

            <div className="summary-item">
              <h4>Subclass</h4>
              <div className="summary-value">
                {subclassData ? subclassData.name : capitalize(characterData.subclass || 'Not selected')}
              </div>
              {subclassData && (
                <div className="summary-features">
                  {subclassData.spellcastTrait && (
                    <><strong>Spellcast Trait:</strong> {subclassData.spellcastTrait}</>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Name Input */}
        <div className="character-name-section">
          <label className="name-input-label">
            <h4>Character Name</h4>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Enter your character's name..."
              className="name-input"
            />
          </label>
        </div>

        {/* Quick Stats Preview */}
        {classData && (
          <div className="stats-preview">
            <h4>Starting Character Stats</h4>
            <div className="stats-grid">
              <div className="stat-item">
                <strong>Level:</strong> 1
              </div>
              <div className="stat-item">
                <strong>Tier:</strong> 1
              </div>
              <div className="stat-item">
                <strong>Hit Points:</strong> {classData.startingHitPoints}
              </div>
              <div className="stat-item">
                <strong>Stress:</strong> {classData.startingStress}
              </div>
              <div className="stat-item">
                <strong>Evasion:</strong> {classData.startingEvasion}
              </div>
            </div>
          </div>
        )}

        {/* Character Traits */}
        {characterData.traits && (
          <div className="traits-summary">
            <h4>Character Traits</h4>
            <div className="traits-grid">
              {Object.entries(characterData.traits).map(([trait, value]) => (
                <div key={trait} className="trait-item">
                  <strong>{trait.charAt(0).toUpperCase() + trait.slice(1)}:</strong>{' '}
                  <span className={`trait-value ${value >= 0 ? 'positive' : 'negative'}`}>
                    {value > 0 ? `+${value}` : value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Experiences */}
        {characterData.experiences && characterData.experiences.length > 0 && (
          <div className="experiences-summary">
            <h4>Experiences</h4>
            <div className="experiences-list">
              {characterData.experiences.map((experience) => (
                <div key={experience.id} className="experience-item">
                  <strong>{experience.name}</strong>
                  <span className="experience-bonus">+{experience.bonus}</span>
                </div>
              ))}
            </div>
            <div className="experiences-note">
              Each experience grants a +{characterData.experiences[0]?.bonus || 2} bonus when relevant to your actions.
            </div>
          </div>
        )}

        {/* Appearance & Personality */}
        {characterData.appearance && Object.keys(characterData.appearance).some(key => characterData.appearance![key as keyof typeof characterData.appearance]) && (
          <div className="appearance-summary">
            <h4>Appearance & Personality</h4>
            <div className="appearance-traits">
              {Object.entries(characterData.appearance).map(([category, value]) =>
                value ? (
                  <div key={category} className="appearance-trait">
                    <strong>{category.charAt(0).toUpperCase() + category.slice(1)}:</strong> {value}
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}

        {/* Starting Choices */}
        {characterData.startingChoices && Object.keys(characterData.startingChoices).length > 0 && (
          <div className="starting-choices-summary">
            <h4>Starting Choices</h4>
            <div className="choices-list">
              {Object.entries(characterData.startingChoices).map(([category, choice]) => (
                <div key={category} className="choice-summary-item">
                  <strong>{category.charAt(0).toUpperCase() + category.slice(1)}:</strong> {choice}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Domain Cards */}
        {domainCards.length > 0 && (
          <div className="domain-cards-summary">
            <h4>Starting Domain Cards</h4>
            <div className="domain-cards-list">
              {domainCards.map((card) => (
                <div key={card.id} className="domain-card-summary-item">
                  <div className="domain-card-header">
                    <strong>{card.name}</strong>
                    <span className="domain-card-domain">{card.domain}</span>
                  </div>
                  <div className="domain-card-type">
                    {card.type} • Level {card.level}
                  </div>
                  <div className="domain-card-description">
                    {card.description}
                  </div>
                  {card.tags.length > 0 && (
                    <div className="domain-card-tags">
                      {card.tags.map((tag, idx) => (
                        <span key={idx} className="domain-card-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Equipment Summary */}
        {characterData.selectedEquipment && (characterData.selectedEquipment.primaryWeapon || characterData.selectedEquipment.armor) && classData?.suggestedEquipment && (
          <div className="equipment-summary">
            <h4>Selected Equipment</h4>
            <div className="equipment-list">
              {characterData.selectedEquipment.primaryWeapon && (
                <div className="equipment-summary-item">
                  ✓ {classData.suggestedEquipment.primaryWeapon}
                </div>
              )}
              {characterData.selectedEquipment.armor && (
                <div className="equipment-summary-item">
                  ✓ {classData.suggestedEquipment.armor}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Background Information */}
        {characterData.backgroundAnswers && characterData.backgroundAnswers.length > 0 && (
          <div className="background-summary">
            <h4>Background</h4>
            <div className="background-answers">
              {characterData.backgroundAnswers.map((item, index) =>
                item.answer.trim() ? (
                  <div key={index} className="background-answer">
                    <div className="background-question">
                      <strong>{item.question}</strong>
                    </div>
                    <div className="background-response">{item.answer}</div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}

        {/* Connection Notes */}
        {characterData.connectionAnswers && characterData.connectionAnswers.length > 0 && (
          <div className="connections-summary">
            <h4>Party Connections</h4>
            <div className="connection-answers">
              {characterData.connectionAnswers.map((item, index) =>
                item.answer.trim() ? (
                  <div key={index} className="connection-answer">
                    <div className="connection-prompt">
                      <strong>{item.prompt}</strong>
                    </div>
                    <div className="connection-response">{item.answer}</div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CharacterSummary;